"use client";

import { useForm, useStore } from "@tanstack/react-form";
import {
  AlertCircle,
  CalendarDays,
  ChartBarDecreasing,
  Check,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Music2,
  Sticker,
  UserRound,
  X,
} from "lucide-react";
import { type MouseEvent, useRef, useState } from "react";
import { toast } from "sonner";
import type { FeedOriginalPost, FeedPost } from "@/actions/post";
import { getTaggableEvents, type TaggableEvent } from "@/actions/post-context";
import { AudienceSelector } from "@/components/audience-selector";
import { GifPickerDrawer } from "@/components/chat/pickers/gif-picker-drawer";
import { FeedCardAvatar } from "@/components/feed/feed-card-header";
import { PostMediaComposer } from "@/components/feed/post-media-composer";
import { MediaTagEditor } from "@/components/feed/post-media-composer/media-tag-editor.client";
import {
  EMPTY_MEDIA_UPLOAD_STATE,
  type MediaUploadState,
  type UploadedMedia,
} from "@/components/image-upload";
import { LocationSearchBox } from "@/components/location-search-box";
import { SongCard } from "@/components/music/song-card";
import { SongPickerDrawer } from "@/components/music/song-picker-drawer";
import { FeedEntityQuoteEmbed } from "@/components/posts/feed-entity-quote-embed";
import { OriginalPostEmbed } from "@/components/posts/original-post-embed";
import { PostEventTag } from "@/components/posts/post-event-tag";
import { PostLinkPreview } from "@/components/posts/post-link-preview";
import { PostLocationTag } from "@/components/posts/post-location-tag";
import { RichPostComposer } from "@/components/posts/rich-post-composer";
import { HiiveStickerPicker } from "@/components/stickers/hiive-sticker-picker";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { EmptyState } from "@/components/ui/empty-state";
import { ListRowSkeleton } from "@/components/ui/list-row-skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FloatingSticker } from "@/components/wallet/sticker-card-3d";
import type {
  MediaData,
  PostLocationTag as PostLocationTagData,
  PostVisibility,
} from "@/db/schema/post";
import type { KlipyItem } from "@/hooks/use-gifs";
import {
  useCreatePollPostMutation,
  useCreatePostMutation,
  useQuoteFeedEntityMutation,
  useRepostPostMutation,
} from "@/hooks/use-posts";
import { formatEventDate } from "@/lib/dates/event-display";
import type { FeedEntityQuoteTarget } from "@/lib/feed/feed-entity-quote";
import { extractFirstFeedUrl } from "@/lib/feed/rich-content";
import { type MediaTag, toMediaAssetSlimForWrite } from "@/lib/media";
import {
  type MusicAttachment,
  musicAttachmentFromSong,
  musicAttachmentInput,
} from "@/lib/music/attachment";
import type { NominatimPlace } from "@/lib/nominatim";
import { uiHaptics } from "@/lib/ui-haptics";
import { findSticker } from "@/lib/wallet/sticker-share";
import { HiiveLogoIcon } from "@/svgs/hiive-logo-icon";
import {
  CAPTION_LIMIT,
  CharRing,
  ComposerBrandRow,
  ComposerMediaBlock,
  type ComposerMediaItem,
  ComposerMusicLine,
  ComposerPollEditor,
  ComposerToolButton,
  GifGlyph,
  MIN_POLL_OPTIONS,
  type PollOptionDraft,
} from "./composer-parts";

type RegularPostDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUser: {
    id: string;
    displayName: string;
    avatarUrl?: string | null;
  };
  onSuccess?: () => void;
  /**
   * When provided, the composer shows a prominent "Post · Plan a session"
   * toggle; choosing the session segment closes the composer and invokes this
   * to launch the activity-session flow.
   */
  onPlanSession?: () => void;
  /**
   * Pre-selected event tag, seeded every time the drawer opens (e.g. a
   * "Post about this event" CTA). The user can still remove or replace it.
   */
  initialEventTag?: TaggableEvent | null;
  /**
   * Pre-picked song, seeded on every open — "Post with this song" from a song
   * sheet. The user can remove or change it.
   */
  initialMusic?: MusicAttachment | null;
  /**
   * Pre-attached Hiive sticker id, seeded on every open — "Share to feed" from
   * a sticker. Only the member's own unlocked stickers can be picked; the
   * server re-checks on publish.
   */
  initialSticker?: string | null;
  /** When present, the same rich composer publishes a post or activity quote. */
  quoteTarget?:
    | {
        kind: "post";
        postId: number;
        post: FeedOriginalPost | FeedPost;
        currentlyReposted: boolean;
        defaultVisibility: PostVisibility;
      }
    | {
        kind: "feed_entity";
        target: FeedEntityQuoteTarget;
        defaultVisibility: PostVisibility;
      }
    | null;
};

type ExternalComposerMedia = Extract<MediaData, { provider: "klipy" }> & {
  composerKind: "gif" | "sticker";
  previewUrl: string;
};

type GifPickerMode = "gif" | "sticker" | null;

// Taps on these keep their own behaviour instead of focusing the caption: the
// caption itself (native caret placement), every control, and the attachments
// (media cards, poll inputs, tag/link chips, the quoted post).
const COMPOSER_BODY_TAP_EXCLUDE =
  'textarea, input, select, button, a, label, [role="button"], [contenteditable="true"], [data-composer-attachments]';

const AUDIENCE_ROW_LABEL: Record<PostVisibility, string> = {
  public: "Everyone can see this",
  private: "Only your buddies can see this",
  close_friends: "Only Close Friends can see this",
};

const createPollOptionDraft = (): PollOptionDraft => ({
  id: Math.random().toString(36).slice(2),
  label: "",
});

const mediaUrlExtension = (url: string, fallback: "webp" | "mp4") => {
  try {
    const parsed = new URL(url);
    const match = parsed.pathname.match(/\.([a-z0-9]+)$/i);
    return match?.[1]?.toLowerCase() ?? fallback;
  } catch {
    return fallback;
  }
};

const mimeTypeFromExtension = (extension: string) => {
  switch (extension) {
    case "gif":
      return "image/gif";
    case "mp4":
      return "video/mp4";
    case "webp":
      return "image/webp";
    default:
      return "image/webp";
  }
};

const stableHexId = (value: string) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(12, "0");
};

const toExternalComposerMedia = (
  item: KlipyItem,
  composerKind: "gif" | "sticker",
  order: number,
): ExternalComposerMedia | null => {
  const imageUrl = item.urls.webp ?? item.urls.gif;
  const videoUrl = imageUrl ? undefined : item.urls.mp4;
  const previewUrl = imageUrl ?? videoUrl;

  if (!previewUrl) return null;

  const extension = mediaUrlExtension(previewUrl, videoUrl ? "mp4" : "webp");
  const assetId = stableHexId(`${composerKind}:${item.slug}:${previewUrl}`);

  return {
    id: `klipy-${composerKind}-${item.slug}`,
    key: `posts/klipy/${composerKind}/${assetId}.${extension}`,
    order,
    provider: "klipy",
    url: previewUrl,
    previewUrl,
    kind: videoUrl ? "video" : "image",
    mimeType: mimeTypeFromExtension(extension),
    title: item.title,
    composerKind,
    // Carry the provider's dimensions so the feed frame reserves the GIF's
    // real (clamped) aspect instead of a fixed placeholder box.
    width: item.width || undefined,
    height: item.height || undefined,
  };
};

function toPostLocationTag(place: NominatimPlace): PostLocationTagData | null {
  const latitude = Number(place.lat);
  const longitude = Number(place.lon);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    displayName: place.name?.trim() || place.display_name.split(",")[0],
    address: place.display_name,
    latitude,
    longitude,
    provider: "nominatim",
    providerPlaceId: String(place.place_id),
    privacyPrecision: "exact",
  };
}

export function RegularPostDrawer({
  open,
  onOpenChange,
  currentUser,
  onSuccess,
  onPlanSession,
  initialEventTag,
  initialMusic = null,
  initialSticker = null,
  quoteTarget = null,
}: RegularPostDrawerProps) {
  const [selectedSticker, setSelectedSticker] = useState<string | null>(
    initialSticker,
  );
  const [stickerPickerOpen, setStickerPickerOpen] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState<MusicAttachment | null>(
    initialMusic,
  );
  const [musicPickerOpen, setMusicPickerOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [mediaUploadState, setMediaUploadState] = useState<MediaUploadState>(
    EMPTY_MEDIA_UPLOAD_STATE,
  );
  const [uploadResetToken, setUploadResetToken] = useState(() =>
    Math.random().toString(36).slice(2),
  );
  const [mediaDrawerOpen, setMediaDrawerOpen] = useState(false);
  // The media sheet's uploader unmounts while closed; it reopens on the draft
  // as it stood when the sheet opened (a snapshot, so the uploader never
  // chases its own emissions while it is open).
  const [mediaDrawerSeed, setMediaDrawerSeed] = useState<UploadedMedia[]>([]);
  // Uploaded still whose people tags are being edited from the draft.
  const [taggingMediaId, setTaggingMediaId] = useState<string | null>(null);
  const [gifPickerMode, setGifPickerMode] = useState<GifPickerMode>(null);
  const [locationDrawerOpen, setLocationDrawerOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] =
    useState<PostLocationTagData | null>(null);
  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const [selectedEventTag, setSelectedEventTag] =
    useState<TaggableEvent | null>(initialEventTag ?? null);
  const [visibility, setVisibility] = useState<PostVisibility>(
    quoteTarget?.defaultVisibility ?? "private",
  );
  // Fetched lazily the first time the event picker opens; null = not loaded.
  const [taggableEvents, setTaggableEvents] = useState<TaggableEvent[] | null>(
    null,
  );
  const [taggableEventsLoading, setTaggableEventsLoading] = useState(false);
  // Re-seed the event tag on every open so a fresh initialEventTag (e.g. a
  // Photos-tab CTA) wins over whatever the previous draft left behind.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSelectedEventTag(initialEventTag ?? null);
      setSelectedMusic(initialMusic ?? null);
      setSelectedSticker(initialSticker ?? null);
      setVisibility(quoteTarget?.defaultVisibility ?? "private");
    }
  }
  const [dismissedLinkUrl, setDismissedLinkUrl] = useState<string | null>(null);
  const [pollEnabled, setPollEnabled] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState<PollOptionDraft[]>(() => [
    createPollOptionDraft(),
    createPollOptionDraft(),
  ]);
  const [externalMedia, setExternalMedia] = useState<ExternalComposerMedia[]>(
    [],
  );
  const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
  const { mutateAsync: createPost, isPending: isMutationPending } =
    useCreatePostMutation(20);
  const { mutateAsync: createPollPost, isPending: isPollMutationPending } =
    useCreatePollPostMutation(20);
  const { mutateAsync: createQuotePost, isPending: isQuoteMutationPending } =
    useRepostPostMutation();
  const {
    mutateAsync: createFeedEntityQuote,
    isPending: isFeedEntityQuotePending,
  } = useQuoteFeedEntityMutation();

  const isSubmitting =
    isMutationPending ||
    isPollMutationPending ||
    isQuoteMutationPending ||
    isFeedEntityQuotePending ||
    isDraftSubmitting;

  // The caption is the only thing on the sheet body you'd tap to type into, so
  // a tap anywhere on the body that isn't a control or an attachment focuses
  // it, caret at the end. focus() must run synchronously inside the tap's own
  // handler: iOS only raises the keyboard for a focus it can tie to a gesture.
  const captionRef = useRef<HTMLTextAreaElement | null>(null);
  const isCaptionBodyTap = (event: MouseEvent<HTMLElement>) =>
    !isSubmitting &&
    event.target instanceof Element &&
    !event.target.closest(COMPOSER_BODY_TAP_EXCLUDE);
  const handleBodyMouseDown = (event: MouseEvent<HTMLElement>) => {
    // Already typing: don't let the tap blur the caption first (on Android
    // that drops and re-raises the keyboard).
    if (
      isCaptionBodyTap(event) &&
      document.activeElement === captionRef.current
    ) {
      event.preventDefault();
    }
  };
  const handleBodyClick = (event: MouseEvent<HTMLElement>) => {
    const caption = captionRef.current;
    if (!caption || !isCaptionBodyTap(event)) return;
    if (document.activeElement === caption) return;
    caption.focus({ preventScroll: true });
    const end = caption.value.length;
    caption.setSelectionRange(end, end);
  };

  const resetDraft = () => {
    setExternalMedia([]);
    setMediaUploadState(EMPTY_MEDIA_UPLOAD_STATE);
    setMediaDrawerOpen(false);
    setMediaDrawerSeed([]);
    setTaggingMediaId(null);
    setGifPickerMode(null);
    setLocationDrawerOpen(false);
    setSelectedLocation(null);
    setEventDrawerOpen(false);
    setSelectedEventTag(null);
    setSelectedMusic(null);
    setMusicPickerOpen(false);
    setSelectedSticker(null);
    setStickerPickerOpen(false);
    setDismissedLinkUrl(null);
    setPollEnabled(false);
    setPollQuestion("");
    setPollOptions([createPollOptionDraft(), createPollOptionDraft()]);
    setIsDraftSubmitting(false);
    setVisibility(quoteTarget?.defaultVisibility ?? "private");
    setUploadResetToken(Math.random().toString(36).slice(2));
    form.reset();
  };

  const form = useForm({
    defaultValues: {
      caption: "",
      media: [] as UploadedMedia[],
    },
    onSubmit: async ({ value }) => {
      if (isDraftSubmitting) {
        return;
      }

      if (isUploading) {
        toast.warning("Please wait for uploads to finish");
        return;
      }

      if (value.media.some((item) => item.isUploading)) {
        toast.warning("Please wait for uploads to finish");
        return;
      }

      if (value.media.some((item) => item.error)) {
        toast.error("Remove failed uploads before sharing.");
        return;
      }

      if (value.caption.length > CAPTION_LIMIT) {
        toast.error(
          `Keep it under ${CAPTION_LIMIT.toLocaleString()} characters`,
        );
        return;
      }

      const readyMedia = value.media
        .filter((item) => item.url && !item.error && !item.isUploading)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const trimmedCaption = value.caption.trim();

      const stickerPayload = selectedSticker
        ? { stickerId: selectedSticker }
        : null;
      const uploadedMediaPayload = readyMedia
        .map((item, index) => toMediaAssetSlimForWrite(item, index))
        .filter(
          (
            item,
          ): item is NonNullable<ReturnType<typeof toMediaAssetSlimForWrite>> =>
            item !== null,
        );
      const externalMediaPayload: MediaData[] = externalMedia.map(
        ({ composerKind: _composerKind, previewUrl: _previewUrl, ...item }) =>
          item,
      );

      const mediaPayload: MediaData[] = [
        ...uploadedMediaPayload,
        ...externalMediaPayload,
      ].map((item, index) => ({ ...item, order: index }));

      const trimmedPollQuestion = pollQuestion.trim();
      const readyPollOptions = pollOptions
        .map((option) => option.label.trim())
        .filter(Boolean);
      const uniquePollOptions = Array.from(new Set(readyPollOptions));

      if (pollEnabled) {
        if (!trimmedPollQuestion) {
          toast.error("Add a poll question");
          return;
        }
        if (uniquePollOptions.length < MIN_POLL_OPTIONS) {
          toast.error("Add at least two unique poll options");
          return;
        }
      }

      if (
        !pollEnabled &&
        !trimmedCaption &&
        mediaPayload.length === 0 &&
        selectedLocation === null &&
        selectedEventTag === null &&
        selectedSticker === null &&
        (quoteTarget ? null : selectedMusic) === null
      ) {
        toast.error(
          "Add text, media, GIF, sticker, a song, a location, or an event",
        );
        return;
      }

      try {
        setIsDraftSubmitting(true);
        if (quoteTarget) {
          const richQuoteBody = {
            quoteContent: trimmedCaption || null,
            mediaUrls: mediaPayload,
            locationTag: selectedLocation,
            contextTag: selectedEventTag
              ? {
                  type: "event_occurrence" as const,
                  id: selectedEventTag.occurrenceId,
                }
              : null,
            poll: pollEnabled
              ? {
                  question: trimmedPollQuestion,
                  options: uniquePollOptions,
                }
              : null,
            sticker: stickerPayload,
            visibility,
          };

          if (quoteTarget.kind === "post") {
            await createQuotePost({
              postId: quoteTarget.postId,
              currentlyReposted: quoteTarget.currentlyReposted,
              isQuote: true,
              ...richQuoteBody,
            });
          } else {
            await createFeedEntityQuote({
              entityType: quoteTarget.target.entityType,
              entityId: quoteTarget.target.entityId,
              ...richQuoteBody,
            });
          }
        } else if (pollEnabled) {
          await createPollPost({
            question: trimmedPollQuestion,
            content: trimmedCaption || null,
            options: uniquePollOptions,
            mediaUrls: mediaPayload,
            locationTag: selectedLocation,
            music: selectedMusic ? musicAttachmentInput(selectedMusic) : null,
            musicPreview: selectedMusic,
            sticker: stickerPayload,
            visibility,
            viewer: {
              id: currentUser.id,
              displayName: currentUser.displayName,
              avatarUrl: currentUser.avatarUrl ?? null,
            },
          });
        } else {
          await createPost({
            type: "user",
            content: trimmedCaption || null,
            mediaUrls: mediaPayload,
            locationTag: selectedLocation,
            music: selectedMusic ? musicAttachmentInput(selectedMusic) : null,
            musicPreview: selectedMusic,
            sticker: stickerPayload,
            contextTag: selectedEventTag
              ? {
                  type: "event_occurrence" as const,
                  id: selectedEventTag.occurrenceId,
                }
              : null,
            visibility,
            status: "published",
            isPinned: false,
            viewer: {
              id: currentUser.id,
              displayName: currentUser.displayName,
              avatarUrl: currentUser.avatarUrl ?? null,
            },
          });
        }

        toast.success(quoteTarget ? "Quote reposted" : "Posted!");
        handleClose();
        onSuccess?.();
      } catch (error) {
        setIsDraftSubmitting(false);
        toast.error(
          error instanceof Error ? error.message : "Failed to create post",
        );
      }
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(resetDraft, 300);
  };

  const handlePlanSession = () => {
    handleClose();
    onPlanSession?.();
  };

  // The draft re-renders with the form (useForm alone does not subscribe), so
  // every attachment below reads the live values.
  const caption = useStore(form.store, (state) => state.values.caption);
  const media = useStore(form.store, (state) => state.values.media);

  const sortedMedia = [...media].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const composerMedia: ComposerMediaItem[] = [
    ...sortedMedia.map((item): ComposerMediaItem => {
      const isVideo = item.kind === "video";
      return {
        id: item.id,
        kind: isVideo ? "video" : "image",
        provider: "r2",
        url: isVideo
          ? (item.url ?? null)
          : (item.url ?? item.poster?.url ?? null),
        posterUrl: item.poster?.url ?? null,
        width: item.width,
        height: item.height,
        isUploading: item.isUploading,
        error: item.error,
        label: isVideo ? "video" : "photo",
        canTag:
          !isVideo && Boolean(item.url) && !item.isUploading && !item.error,
        tagCount: item.tags?.length ?? 0,
      };
    }),
    ...externalMedia.map(
      (item): ComposerMediaItem => ({
        id: item.id,
        kind: item.kind,
        provider: "klipy",
        url: item.previewUrl,
        width: item.width,
        height: item.height,
        label: item.composerKind === "gif" ? "GIF" : "sticker",
        canTag: false,
        tagCount: 0,
      }),
    ),
  ];
  const hasMedia = composerMedia.length > 0;
  const taggableMedia = composerMedia.filter((item) => item.canTag);
  const taggedPeopleCount = taggableMedia.reduce(
    (total, item) => total + item.tagCount,
    0,
  );
  const taggingItem = taggingMediaId
    ? (media.find((item) => item.id === taggingMediaId) ?? null)
    : null;

  const mediaPending = isUploading || media.some((item) => item.isUploading);
  const mediaHasErrors = media.some((item) => Boolean(item.error));
  // The uploader can't be closed mid-upload/crop: its sheet unmounts when
  // closed, and an upload that finishes after that has nowhere to land.
  const mediaSheetBusy =
    mediaUploadState.isUploading ||
    Boolean(mediaUploadState.isReviewing) ||
    Boolean(mediaUploadState.isCropping);
  const isOverLimit = caption.length > CAPTION_LIMIT;
  const hasValidPoll =
    !pollEnabled ||
    (pollQuestion.trim().length > 0 &&
      new Set(pollOptions.map((option) => option.label.trim()).filter(Boolean))
        .size >= MIN_POLL_OPTIONS);
  const hasReadyMedia = media.some(
    (item) => item.url && !item.error && !item.isUploading,
  );
  const canSubmit =
    !isSubmitting &&
    !mediaPending &&
    !mediaHasErrors &&
    !isOverLimit &&
    hasValidPoll &&
    (caption.trim().length > 0 ||
      hasReadyMedia ||
      externalMedia.length > 0 ||
      selectedLocation !== null ||
      selectedEventTag !== null ||
      selectedSticker !== null ||
      (selectedMusic !== null && !quoteTarget) ||
      (pollEnabled && pollQuestion.trim().length > 0));
  const linkPreview = extractFirstFeedUrl(caption);
  const showLinkPreview =
    linkPreview !== null && linkPreview.url !== dismissedLinkUrl;
  // Quotes don't carry a song (the quote payload has no music).
  const draftMusic = quoteTarget ? null : selectedMusic;
  const draftSticker = findSticker(selectedSticker);

  const openMediaDrawer = () => {
    setMediaDrawerSeed(form.state.values.media);
    setMediaDrawerOpen(true);
  };

  const handleRemoveMedia = (id: string) => {
    if (externalMedia.some((entry) => entry.id === id)) {
      setExternalMedia((previous) =>
        previous.filter((entry) => entry.id !== id),
      );
      return;
    }
    form.setFieldValue(
      "media",
      form.state.values.media.filter((item) => item.id !== id),
    );
  };

  const handleTagsChange = (id: string, tags: MediaTag[]) => {
    form.setFieldValue(
      "media",
      form.state.values.media.map((item) => {
        if (item.id !== id) return item;
        if (tags.length > 0) return { ...item, tags };
        const { tags: _tags, ...rest } = item;
        return rest;
      }),
    );
  };

  const handleSelectGif = (item: KlipyItem, kind: "gif" | "sticker") => {
    const nextMedia = toExternalComposerMedia(
      item,
      kind,
      form.state.values.media.length + externalMedia.length,
    );
    if (!nextMedia) {
      toast.error(`Couldn't add ${kind === "gif" ? "GIF" : "sticker"}`);
      return;
    }

    setExternalMedia((previous) => {
      if (previous.some((entry) => entry.id === nextMedia.id)) {
        toast.message(`${kind === "gif" ? "GIF" : "Sticker"} already added`);
        return previous;
      }
      return [...previous, nextMedia];
    });
  };

  const handleOpenPoll = () => {
    setMediaDrawerOpen(false);
    setGifPickerMode(null);
    // Poll posts don't support event tags; drop the tag visibly rather than
    // letting it silently vanish at submit time.
    if (selectedEventTag) {
      setSelectedEventTag(null);
      toast.message("Event tags aren't supported on polls");
    }
    setPollEnabled(true);
  };

  const handleOpenEventDrawer = () => {
    setEventDrawerOpen(true);
    // Lazy-load on first open; a failed fetch leaves the list null so the
    // next open retries.
    if (taggableEvents === null && !taggableEventsLoading) {
      setTaggableEventsLoading(true);
      getTaggableEvents()
        .then((result) => {
          if (result.ok) {
            setTaggableEvents(result.data);
          } else {
            toast.error(result.message);
          }
        })
        .catch(() => {
          toast.error("Couldn't load your events");
        })
        .finally(() => {
          setTaggableEventsLoading(false);
        });
    }
  };

  const firstTaggable = taggableMedia[0] ?? null;
  const showTagPeopleRow = firstTaggable !== null;

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(val) => {
          if (!val) handleClose();
          else onOpenChange(true);
        }}
        modal
        // Neither the handle nor Cancel may drop a post mid-publish.
        dismissible={!isSubmitting}
      >
        {/* Opaque page background in BOTH themes: the base DrawerContent is
            `dark:bg-black/90` + blur, which an unprefixed `bg-background` can't
            override, so the feed glowed through the composer body in dark. */}
        <DrawerContent
          className="border-t border-border bg-background outline-none dark:bg-background"
          style={{
            height: "calc(100dvh - env(safe-area-inset-top))",
            maxHeight: "calc(100dvh - env(safe-area-inset-top))",
          }}
        >
          <DrawerTitle className="sr-only">
            {quoteTarget ? "Quote repost" : "Create Post"}
          </DrawerTitle>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
            className="flex min-h-0 flex-1 flex-col"
            data-testid={quoteTarget ? "quote-post-composer" : "post-composer"}
          >
            <div className="relative mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col">
              {/* X's composer bar: Cancel left, the Post pill right. The
                  1fr / auto / 1fr grid keeps the Post · Plan a session switch
                  centred whatever either side grows to. */}
              <header className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 pt-1 pb-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="justify-self-start py-1.5 text-[15px] text-text-primary transition-opacity active:opacity-60 disabled:opacity-40"
                >
                  Cancel
                </button>
                {onPlanSession ? (
                  <div className="flex items-center rounded-full bg-surface-alt p-0.5 text-[13px] font-semibold">
                    <span
                      aria-current="true"
                      className="rounded-full bg-background px-3 py-1.5 text-text-primary shadow-sm"
                    >
                      Post
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        uiHaptics.select();
                        handlePlanSession();
                      }}
                      disabled={isSubmitting}
                      className="rounded-full px-3 py-1.5 text-text-secondary transition-colors hover:text-text-primary disabled:opacity-50"
                    >
                      Plan a session
                    </button>
                  </div>
                ) : (
                  <span aria-hidden="true" />
                )}
                <Button
                  type="submit"
                  size="sm"
                  disabled={!canSubmit}
                  className="h-8 min-w-16 justify-self-end rounded-full bg-brand px-4 text-[15px] font-bold text-text-inverse hover:bg-brand-hover dark:bg-brand dark:hover:bg-brand-hover"
                >
                  {isSubmitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    "Post"
                  )}
                </Button>
              </header>

              {/* The draft is laid out as the post it will become: an avatar
                  column, and one content column for the text and everything
                  attached to it. A tap on any empty part of it types. A plain
                  scroller, not ScrollArea: its content box is
                  `min-width: fit-content`, so the media strip would widen the
                  whole column instead of scrolling sideways. */}
              {/* biome-ignore lint/a11y/noStaticElementInteractions: focus forwarding only; the caption itself is the control. */}
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users reach the caption directly. */}
              <div
                className="no-scrollbar min-h-0 flex-1 cursor-text overflow-x-hidden overflow-y-auto overscroll-contain"
                onMouseDown={handleBodyMouseDown}
                onClick={handleBodyClick}
              >
                <div className="flex gap-2.5 px-3 pt-2 pb-8">
                  <div className="shrink-0">
                    <FeedCardAvatar
                      displayName={currentUser.displayName}
                      avatarUrl={currentUser.avatarUrl}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <form.Field name="caption">
                      {(field) => (
                        <RichPostComposer
                          textareaRef={captionRef}
                          currentUser={currentUser}
                          value={field.state.value}
                          onChange={field.handleChange}
                          placeholder={
                            quoteTarget ? "Add a comment" : "What's happening?"
                          }
                          disabled={isSubmitting}
                          rows={1}
                          showAvatar={false}
                          showName={false}
                          autoGrow
                          containerClassName="block"
                          // 19px on a 26px line: the first line's centre
                          // meets the 40px avatar's (7 + 13 = 20).
                          textareaClassName="min-h-[33px] pt-[7px] text-[19px] leading-[26px] outline-none placeholder:text-text-tertiary focus-visible:ring-offset-0 md:text-[19px]"
                        />
                      )}
                    </form.Field>

                    <div
                      data-composer-attachments=""
                      className="mt-3 flex cursor-auto flex-col gap-2.5 empty:hidden"
                    >
                      {draftMusic && hasMedia ? (
                        <ComposerMusicLine
                          music={draftMusic}
                          onChange={() => setMusicPickerOpen(true)}
                          onRemove={() => setSelectedMusic(null)}
                        />
                      ) : null}

                      <ComposerMediaBlock
                        items={composerMedia}
                        onRemove={handleRemoveMedia}
                        onTag={setTaggingMediaId}
                        onEdit={openMediaDrawer}
                      />

                      {draftSticker ? (
                        <div className="relative w-fit">
                          <button
                            type="button"
                            aria-label={`Change sticker: ${draftSticker.title}`}
                            onClick={() => setStickerPickerOpen(true)}
                            className="-mx-3 block rounded-3xl"
                          >
                            <FloatingSticker
                              sticker={draftSticker}
                              width={150}
                            />
                          </button>
                          <button
                            type="button"
                            aria-label="Remove sticker"
                            onClick={() => setSelectedSticker(null)}
                            className="absolute -top-2 -right-3 flex size-7 items-center justify-center rounded-full border border-border bg-surface text-text-primary shadow-md"
                          >
                            <X className="size-4" />
                          </button>
                        </div>
                      ) : null}

                      {showTagPeopleRow ? (
                        <ComposerBrandRow
                          icon={<UserRound className="size-4" />}
                          onClick={() => setTaggingMediaId(firstTaggable.id)}
                        >
                          {taggedPeopleCount > 0
                            ? `${taggedPeopleCount} ${
                                taggedPeopleCount === 1 ? "person" : "people"
                              } tagged`
                            : "Tag people"}
                        </ComposerBrandRow>
                      ) : null}

                      {pollEnabled ? (
                        <ComposerPollEditor
                          question={pollQuestion}
                          onQuestionChange={setPollQuestion}
                          options={pollOptions}
                          onOptionsChange={setPollOptions}
                          onRemove={() => setPollEnabled(false)}
                          createOption={createPollOptionDraft}
                          disabled={isSubmitting}
                        />
                      ) : null}

                      {showLinkPreview && linkPreview ? (
                        <PostLinkPreview
                          link={linkPreview}
                          compact
                          onDismiss={() => setDismissedLinkUrl(linkPreview.url)}
                        />
                      ) : null}

                      {/* A song with no photos is the post's song card. */}
                      {draftMusic && !hasMedia ? (
                        <div className="relative">
                          <SongCard
                            music={draftMusic}
                            surface="post"
                            onOpen={() => setMusicPickerOpen(true)}
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedMusic(null)}
                            aria-label="Remove song"
                            className="absolute top-2 right-2 z-10 flex size-6 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-md transition-transform duration-150 ease-out-strong active:scale-90"
                          >
                            <X className="size-3.5" strokeWidth={2.5} />
                          </button>
                        </div>
                      ) : null}

                      {selectedEventTag || selectedLocation ? (
                        <div className="flex flex-wrap gap-1.5">
                          {selectedEventTag ? (
                            <PostEventTag
                              tag={{
                                type: "event_occurrence",
                                occurrenceId: selectedEventTag.occurrenceId,
                                eventId: selectedEventTag.eventId,
                                eventName: selectedEventTag.eventName,
                                eventSlug: selectedEventTag.eventSlug,
                                hiiveSlug: selectedEventTag.hiiveSlug,
                                startTime: selectedEventTag.startTime,
                              }}
                              compact
                              onRemove={() => setSelectedEventTag(null)}
                            />
                          ) : null}
                          {selectedLocation ? (
                            <PostLocationTag
                              location={selectedLocation}
                              compact
                              asLink={false}
                              onRemove={() => setSelectedLocation(null)}
                            />
                          ) : null}
                        </div>
                      ) : null}

                      {/* The quoted card exactly as the feed will show it —
                          inert, so a tap can't navigate away from the draft. */}
                      {quoteTarget ? (
                        <div inert className="select-none">
                          {quoteTarget.kind === "post" ? (
                            <OriginalPostEmbed
                              originalPost={quoteTarget.post}
                              layout={hasMedia ? "row" : "card"}
                            />
                          ) : (
                            <FeedEntityQuoteEmbed target={quoteTarget.target} />
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* X's footer: who can see it (brand row), a hairline, then the
                  toolbar with the character ring at its end. */}
              <div className="bg-background px-3 pb-[max(env(safe-area-inset-bottom),0.5rem)]">
                <div className="flex min-h-10 items-center justify-between gap-2">
                  <AudienceSelector
                    value={visibility}
                    onChange={(next) => setVisibility(next as PostVisibility)}
                    options={[
                      {
                        value: "private",
                        label: "Buddies only",
                        description: "Only your buddies can see this post.",
                      },
                      { value: "close_friends" },
                      { value: "public" },
                    ]}
                    variant="inline"
                    triggerLabel={AUDIENCE_ROW_LABEL[visibility]}
                    disabled={isSubmitting}
                  />

                  {mediaPending ? (
                    <span className="flex shrink-0 items-center gap-1 text-xs text-text-tertiary">
                      <Loader2 className="size-3.5 animate-spin" />
                      Uploading
                    </span>
                  ) : mediaHasErrors ? (
                    <span className="flex shrink-0 items-center gap-1 text-xs text-error">
                      <AlertCircle className="size-3.5" />
                      Remove failed uploads
                    </span>
                  ) : null}
                </div>

                <div className="h-px bg-border" />

                <div className="-mx-1.5 flex items-center pt-1">
                  <div className="no-scrollbar flex min-w-0 flex-1 items-center overflow-x-auto">
                    <ComposerToolButton
                      label="Add photos or video"
                      active={mediaDrawerOpen}
                      disabled={isSubmitting}
                      onClick={openMediaDrawer}
                    >
                      <ImageIcon className="size-5" strokeWidth={1.75} />
                    </ComposerToolButton>
                    <ComposerToolButton
                      label="Add GIF"
                      active={gifPickerMode === "gif"}
                      disabled={isSubmitting}
                      onClick={() => setGifPickerMode("gif")}
                    >
                      <GifGlyph className="size-5" />
                    </ComposerToolButton>
                    <ComposerToolButton
                      label="Add sticker"
                      active={gifPickerMode === "sticker"}
                      disabled={isSubmitting}
                      onClick={() => setGifPickerMode("sticker")}
                    >
                      <Sticker className="size-5" strokeWidth={1.75} />
                    </ComposerToolButton>
                    <ComposerToolButton
                      label={
                        selectedSticker
                          ? "Change Hiive sticker"
                          : "Share a Hiive sticker"
                      }
                      active={selectedSticker !== null || stickerPickerOpen}
                      disabled={isSubmitting}
                      onClick={() => setStickerPickerOpen(true)}
                    >
                      <HiiveLogoIcon
                        width={19}
                        height={19}
                        color="currentColor"
                        aria-hidden="true"
                      />
                    </ComposerToolButton>
                    {quoteTarget ? null : (
                      <ComposerToolButton
                        label={selectedMusic ? "Change song" : "Add music"}
                        active={selectedMusic !== null}
                        disabled={isSubmitting}
                        onClick={() => setMusicPickerOpen(true)}
                      >
                        <Music2 className="size-5" strokeWidth={1.75} />
                      </ComposerToolButton>
                    )}
                    <ComposerToolButton
                      label="Create poll"
                      active={pollEnabled}
                      disabled={isSubmitting}
                      onClick={handleOpenPoll}
                    >
                      <ChartBarDecreasing
                        className="size-5"
                        strokeWidth={1.75}
                      />
                    </ComposerToolButton>
                    <ComposerToolButton
                      label="Add location"
                      active={selectedLocation !== null}
                      disabled={isSubmitting}
                      onClick={() => setLocationDrawerOpen(true)}
                    >
                      <MapPin className="size-5" strokeWidth={1.75} />
                    </ComposerToolButton>
                    <ComposerToolButton
                      label="Tag event"
                      active={selectedEventTag !== null}
                      disabled={isSubmitting || pollEnabled}
                      onClick={handleOpenEventDrawer}
                    >
                      <CalendarDays className="size-5" strokeWidth={1.75} />
                    </ComposerToolButton>
                  </div>
                  {caption.length > 0 ? (
                    <span className="flex shrink-0 items-center pr-2 pl-1">
                      <CharRing value={caption.length} />
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </form>
          {/* Inside the sheet's React tree on purpose: the tag editor is a
              Base UI dialog portaled to <body>, and vaul (Radix) only counts a
              press as "inside" when it bubbles through its content in the
              React tree. Rendered beside the Drawer, any tap in the editor
              dismissed the composer and threw the draft away. */}
          {taggingItem?.url ? (
            <MediaTagEditor
              open
              onOpenChange={(next) => {
                if (!next) setTaggingMediaId(null);
              }}
              imageUrl={taggingItem.url}
              tags={taggingItem.tags ?? []}
              currentUserId={currentUser.id}
              onChange={(tags) => handleTagsChange(taggingItem.id, tags)}
            />
          ) : null}
        </DrawerContent>
      </Drawer>

      <Drawer
        open={mediaDrawerOpen}
        onOpenChange={(next) => {
          // The uploader unmounts with its sheet; never strand an upload.
          if (!next && mediaSheetBusy) return;
          setMediaDrawerOpen(next);
        }}
        nested
        modal
        dismissible={!mediaSheetBusy}
      >
        <DrawerContent
          className="border-t border-border outline-none"
          style={{
            height: "calc(100dvh - env(safe-area-inset-top))",
            maxHeight: "calc(100dvh - env(safe-area-inset-top))",
          }}
        >
          <DrawerTitle className="sr-only">Add Media</DrawerTitle>
          <div className="mx-auto flex h-full w-full max-w-lg flex-col">
            <header className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="font-semibold text-text-primary">Add media</h3>
              <Button
                type="button"
                size="sm"
                disabled={mediaSheetBusy}
                onClick={() => setMediaDrawerOpen(false)}
                className="h-9 rounded-full bg-brand px-4 font-semibold text-text-inverse hover:bg-brand-hover dark:bg-brand dark:hover:bg-brand-hover"
              >
                {mediaUploadState.isUploading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  "Done"
                )}
              </Button>
            </header>
            <ScrollArea className="min-h-0 flex-1">
              <div className="p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                <form.Field name="media">
                  {(field) => (
                    <PostMediaComposer
                      key={uploadResetToken}
                      maxImages={6}
                      onImagesChange={field.handleChange}
                      onUploadingChange={setIsUploading}
                      onUploadStateChange={setMediaUploadState}
                      initialImages={mediaDrawerSeed}
                      uploadScope={{ domain: "posts", slot: "media" }}
                      currentUserId={currentUser.id}
                    />
                  )}
                </form.Field>
              </div>
            </ScrollArea>
          </div>
        </DrawerContent>
      </Drawer>

      <SongPickerDrawer
        open={musicPickerOpen}
        onOpenChange={setMusicPickerOpen}
        nested
        selectedSongId={selectedMusic?.songId ?? null}
        title="Add music"
        subtitle="Plays over your photos — tap a song to preview"
        onPick={(song) => setSelectedMusic(musicAttachmentFromSong(song))}
      />

      <HiiveStickerPicker
        open={stickerPickerOpen}
        onOpenChange={setStickerPickerOpen}
        nested
        selectedId={selectedSticker}
        onSelect={setSelectedSticker}
      />

      <GifPickerDrawer
        open={gifPickerMode !== null}
        initialTab={gifPickerMode === "sticker" ? "stickers" : "gifs"}
        nested
        onOpenChange={(next) => {
          if (!next) setGifPickerMode(null);
        }}
        onSelect={(item, kind) => handleSelectGif(item, kind)}
      />

      <Drawer
        open={locationDrawerOpen}
        onOpenChange={setLocationDrawerOpen}
        nested
        modal
      >
        <DrawerContent
          className="border-t border-border outline-none"
          style={{ height: "80dvh", maxHeight: "80dvh" }}
        >
          <DrawerTitle className="sr-only">Add location</DrawerTitle>
          <div className="mx-auto flex h-full w-full max-w-lg flex-col px-4 pt-2 pb-[max(env(safe-area-inset-bottom),2rem)]">
            <h3 className="mb-3 text-center font-semibold text-text-primary">
              Add location
            </h3>
            {/* Tall drawer so the search results dropdown has room to render. */}
            <LocationSearchBox
              searchProfile="mixed"
              placeholder="Search places, venues, cities…"
              onSelect={(place) => {
                const location = toPostLocationTag(place);
                if (!location) {
                  toast.error("Couldn't use that location");
                  return;
                }
                setSelectedLocation(location);
                setLocationDrawerOpen(false);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={eventDrawerOpen}
        onOpenChange={setEventDrawerOpen}
        nested
        modal
      >
        <DrawerContent
          className="border-t border-border outline-none"
          style={{ height: "80dvh", maxHeight: "80dvh" }}
        >
          <DrawerTitle className="sr-only">Tag event</DrawerTitle>
          <div className="mx-auto flex h-full w-full max-w-lg flex-col px-4 pt-2 pb-[max(env(safe-area-inset-bottom),2rem)]">
            <h3 className="mb-3 text-center font-semibold text-text-primary">
              Tag event
            </h3>
            <ScrollArea className="min-h-0 flex-1">
              {taggableEventsLoading || taggableEvents === null ? (
                <ListRowSkeleton
                  rowCount={5}
                  avatarClassName="h-9 w-9 rounded-lg"
                />
              ) : taggableEvents.length === 0 ? (
                <EmptyState
                  icon={CalendarDays}
                  title="No events to tag"
                  description="Book an event and it'll show up here."
                  compact
                />
              ) : (
                <div className="pb-2">
                  {taggableEvents.map((item) => {
                    const isSelected =
                      selectedEventTag?.occurrenceId === item.occurrenceId;
                    return (
                      <button
                        key={item.occurrenceId}
                        type="button"
                        onClick={() => {
                          // Single event tag per post — selecting replaces.
                          setSelectedEventTag(item);
                          setEventDrawerOpen(false);
                        }}
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-surface-alt active:bg-surface-alt"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                          <CalendarDays className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-text-primary">
                            {item.eventName}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-text-secondary">
                            {item.hiiveName} ·{" "}
                            {formatEventDate(
                              new Date(item.startTime),
                              "EEE, d MMM",
                            )}
                            {item.slotLabel ? ` · ${item.slotLabel}` : ""}
                          </span>
                        </span>
                        {isSelected ? (
                          <Check className="h-4 w-4 shrink-0 text-brand" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
